import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Mail, Phone, Linkedin } from 'lucide-react';
import { getWpImageSrc } from '../../../lib/wpMedia';

/**
 * Single team member card (same layout as Team page). Used by TeamMemberSection and AboutTeamSection.
 */
export function TeamMemberCard({ person, index, isInView, showIcons = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.05 + index * 0.04 }}
      className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)] transition-shadow text-center"
    >
      <div className="aspect-square md:aspect-[2/3] bg-gray-200 relative overflow-hidden">
        {person.imageUrl && (
          <img
            src={getWpImageSrc(person.imageUrl) || person.imageUrl}
            alt={person.name}
            className="w-full h-full object-cover object-top md:object-center"
          />
        )}
        {person.secondaryImageUrl && (
          <img
            src={getWpImageSrc(person.secondaryImageUrl) || person.secondaryImageUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-top md:object-center opacity-0 transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[opacity] group-hover:opacity-100"
          />
        )}
        {!person.imageUrl && !person.secondaryImageUrl && (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl font-bold">
            {person.name.charAt(0)}
          </div>
        )}
      </div>
      <div className="p-4 md:p-6">
        <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight m-0 mb-0.5 md:mb-1">{person.name}</h3>
        {person.title && (
          <p className="text-xs md:text-sm text-brand font-medium mb-1 md:mb-2">{person.title}</p>
        )}
        {person.fact && (
          <p className="text-xs md:text-sm text-gray-600 leading-relaxed italic mb-2 md:mb-4 line-clamp-3 md:line-clamp-none">&ldquo;{person.fact}&rdquo;</p>
        )}
        {showIcons && (person.email || person.phone || person.linkedin) && (
          <div className="flex items-center justify-center gap-2 md:gap-3 text-gray-500">
            {person.email && (
              <a
                href={`mailto:${person.email}`}
                className="inline-flex items-center justify-center hover:text-brand transition-colors"
                aria-label={`Email ${person.name}`}
              >
                <Mail className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </a>
            )}
            {person.phone && (
              <a
                href={`tel:${person.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center hover:text-brand transition-colors"
                aria-label={`Phone ${person.name}`}
              >
                <Phone className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </a>
            )}
            {person.linkedin && (
              <a
                href={person.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center hover:text-brand transition-colors"
                aria-label={`LinkedIn ${person.name}`}
              >
                <Linkedin className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

const personShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  fact: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  linkedin: PropTypes.string,
  imageUrl: PropTypes.string,
  secondaryImageUrl: PropTypes.string,
  databaseId: PropTypes.number,
});

TeamMemberCard.propTypes = {
  person: personShape.isRequired,
  index: PropTypes.number.isRequired,
  isInView: PropTypes.bool.isRequired,
  showIcons: PropTypes.bool,
};

TeamMemberCard.defaultProps = {
  showIcons: true,
};

/**
 * Team member cards based on About team section.
 */
export function TeamMemberSection({ teamMembers = [], emptyMessage = null }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  if (!teamMembers.length) {
    if (emptyMessage) {
      return (
        <section className="section-rhythm bg-gray-50">
          <div className="max-w-6xl mx-auto px-6 text-center text-gray-500">
            <p className="text-lg">{emptyMessage}</p>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section ref={ref} className="section-rhythm bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
          {teamMembers.map((person, index) => (
            <TeamMemberCard
              key={person.databaseId ?? person.name + index}
              person={person}
              index={index}
              isInView={isInView}
              showIcons
            />
          ))}
        </div>
      </div>
    </section>
  );
}

TeamMemberSection.propTypes = {
  teamMembers: PropTypes.arrayOf(personShape),
  emptyMessage: PropTypes.string,
};

TeamMemberSection.defaultProps = {
  teamMembers: [],
  emptyMessage: null,
};
